import { type FunctionComponent } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';
import { logout, loginWithProvider, getAvailableProviders } from '../api/auth';
import { Button } from './Button';
import { EmailAuthModal } from './EmailAuthModal';
import { useAuth } from '../contexts/AuthContext';
import { navigateTo } from '../App';

/**
 * Provider icons as inline SVGs
 */
const ProviderIcon: FunctionComponent<{ provider: string }> = ({ provider }) => {
  const iconClass = "w-5 h-5 flex-shrink-0";

  switch (provider.toLowerCase()) {
    case 'google':
      return (
        <svg className={iconClass} viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
        </svg>
      );

    case 'github':
      return (
        <svg className={iconClass} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
        </svg>
      );

    case 'facebook':
      return (
        <svg className={iconClass} viewBox="0 0 24 24" fill="#1877F2">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
      );

    case 'apple':
      return (
        <svg className={iconClass} viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
        </svg>
      );

    case 'keycloak':
      return (
        <svg className={iconClass} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" />
        </svg>
      );

    default:
      return (
        <svg className={iconClass} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" />
        </svg>
      );
  }
};

/**
 * AuthButton - Displays authentication status and provides sign in/out actions
 * Shows current user email when authenticated, or dropdown of available providers when not
 */
export function AuthButton(): JSX.Element {
  const { user, loading, refreshUser } = useAuth();
  const [providers, setProviders] = useState<string[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    getAvailableProviders()
      .then((providerList) => setProviders(providerList))
      .catch(() => setProviders([]));
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
    return undefined;
  }, [showDropdown]);

  const handleLogout = async (): Promise<void> => {
    await logout();
    await refreshUser();
  };

  const handleMySecrets = (): void => {
    navigateTo('/my-secrets');
  };

  const handleProviderSelect = (provider: string): void => {
    setShowDropdown(false);
    loginWithProvider(provider);
  };

  const capitalizeProvider = (provider: string): string => {
    return provider.charAt(0).toUpperCase() + provider.slice(1);
  };

  if (loading) {
    return <div className="text-sm text-gray-500 dark:text-gray-400">Loading...</div>;
  }

  if (user?.authenticated && user.email) {
    return (
      <div className="flex items-center gap-3">
        <span className="text-sm text-gray-300 dark:text-gray-400 transition-colors duration-200">{user.email}</span>
        <Button variant="secondary" size="sm" onClick={handleMySecrets}>
          My Secrets
        </Button>
        <Button variant="secondary" size="sm" onClick={handleLogout}>
          Sign Out
        </Button>
      </div>
    );
  }

  // No providers configured
  if (providers.length === 0) {
    return <div className="text-sm text-gray-500 dark:text-gray-400">No auth providers configured</div>;
  }

  // Single provider - show simple button
  if (providers.length === 1 && providers[0]) {
    const provider = providers[0];
    return (
      <Button
        variant="secondary"
        size="sm"
        onClick={() => loginWithProvider(provider)}
        className="flex items-center gap-2"
      >
        <ProviderIcon provider={provider} />
        Sign in with {capitalizeProvider(provider)}
      </Button>
    );
  }

  // Multiple providers - show dropdown
  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => setShowDropdown(!showDropdown)}
      >
        Sign in (optional) ▾
      </Button>

      {showDropdown && (
        <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black dark:ring-gray-700 ring-opacity-5 z-50 transition-colors duration-200">
          <div className="py-1" role="menu">
            <button
              onClick={() => {
                setShowDropdown(false);
                setShowEmailModal(true);
              }}
              className="flex items-center gap-3 w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100 transition-colors duration-200"
              role="menuitem"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span>Sign in with email</span>
            </button>
            {providers.length > 0 && (
              <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
            )}
            {providers.map((provider) => (
              <button
                key={provider}
                onClick={() => handleProviderSelect(provider)}
                className="flex items-center gap-3 w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100 transition-colors duration-200"
                role="menuitem"
              >
                <ProviderIcon provider={provider} />
                <span>Sign in with {capitalizeProvider(provider)}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <EmailAuthModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
      />
    </div>
  );
}
